import React, { useRef, useEffect, useImperativeHandle, forwardRef, useState, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ── Emma Avatar (TalkingHead default brunette) ─
const BRUNETTE_URL = 'https://cdn.jsdelivr.net/gh/met4citizen/TalkingHead@main/avatars/brunette.glb';

const AVATARS = {
  nl: {
    url: BRUNETTE_URL,
    name: 'Emma',
    body: 'F',
    avatarMood: 'neutral',
    lipsyncLang: 'en',
  },
  en: {
    url: BRUNETTE_URL,
    name: 'Emma',
    body: 'F',
    avatarMood: 'neutral',
    lipsyncLang: 'en',
  },
};

// Warm backgrounds matching the portal theme
const BACKGROUNDS = {
  warm: 'linear-gradient(145deg, #FFF9F0 0%, #FFF5EB 50%, #FFEEDD 100%)',
  warmSolid: '#FFF8F0',
  dark: '#0f172a',
};

const TalkingAvatar = forwardRef(({
  lang = 'nl',
  visible = true,
  showNameBadge = true,
  cameraView = 'upper',
  background = BACKGROUNDS.warmSolid,
}, ref) => {
  const containerRef = useRef(null);
  const headRef = useRef(null);
  const [status, setStatus] = useState('loading');
  const avatarLang = lang === 'nl' ? 'nl' : 'en';
  const config = AVATARS[avatarLang];

  const waitForTalkingHead = useCallback(() => {
    return new Promise((resolve) => {
      if (window.TalkingHeadLoaded) {
        resolve(window.TalkingHeadClass || null);
        return;
      }
      const handler = () => {
        window.removeEventListener('talkinghead-ready', handler);
        resolve(window.TalkingHeadClass || null);
      };
      window.addEventListener('talkinghead-ready', handler);
      setTimeout(() => {
        window.removeEventListener('talkinghead-ready', handler);
        resolve(null);
      }, 25000);
    });
  }, []);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setStatus('loading');

    const init = async () => {
      try {
        const TalkingHead = await waitForTalkingHead();
        if (cancelled) return;
        if (!containerRef.current || !TalkingHead) {
          setStatus('error');
          return;
        }

        const head = new TalkingHead(containerRef.current, {
          ttsEndpoint: null,
          lipsyncModules: ['en'],
          cameraView: cameraView,
          cameraRotateEnable: false,
          cameraPanEnable: false,
          cameraZoomEnable: false,
          // Warm, natural lighting for the custom Emma model
          lightAmbientColor: '#FFF5E6',
          lightAmbientIntensity: 3.0,
          lightDirectColor: '#FFFFFF',
          lightDirectIntensity: 15,
          lightSpotIntensity: 0,
          modelFPS: 30,
        });

        if (cancelled) return;

        const { name, ...avatarOpts } = config;

        await head.showAvatar(avatarOpts, (ev) => {
          if (ev.lengthComputable) {
            setStatus(`${Math.round((ev.loaded / ev.total) * 100)}%`);
          }
        });

        if (!cancelled) {
          headRef.current = head;
          try { head.start(); } catch (_) {}
          try { head.setMood('neutral'); } catch (_) {}
          setStatus('ready');
        }
      } catch (err) {
        console.error('[TalkingAvatar]', err);
        if (!cancelled) {
          setStatus('error');
        }
      }
    };

    init();
    return () => {
      cancelled = true;
      if (containerRef.current) containerRef.current.innerHTML = '';
      headRef.current = null;
    };
  }, [visible, avatarLang, config, cameraView, waitForTalkingHead]);

  useImperativeHandle(ref, () => ({
    speakAudio: async (audioBlob, syncData = null) => {
      const head = headRef.current;
      if (!head || !audioBlob) return false;
      try {
        const arrayBuffer = await audioBlob.arrayBuffer();
        const audioBuffer = await head.audioCtx.decodeAudioData(arrayBuffer);

        let words, wtimes, wdurations;
        if (syncData?.words?.length > 0) {
          words = syncData.words;
          wtimes = syncData.wtimes;
          wdurations = syncData.wdurations;
        } else {
          const text = (typeof syncData === 'string' ? syncData : '').trim();
          const durationMs = audioBuffer.duration * 1000;
          const w = text.split(/\s+/).filter(Boolean);
          if (w.length > 0) {
            const totalChars = (Array.isArray(w) ? w : []).reduce((sum, word) => sum + word.length, 0) || 1;
            words = w;
            wtimes = [];
            wdurations = [];
            let cursor = 0;
            for (const word of w) {
              const proportion = word.length / totalChars;
              const dur = Math.round(durationMs * proportion);
              wtimes.push(Math.round(cursor));
              wdurations.push(dur);
              cursor += dur;
            }
          } else {
            words = ['...'];
            wtimes = [0];
            wdurations = [Math.round(audioBuffer.duration * 1000)];
          }
        }

        head.speakAudio({
          audio: audioBuffer,
          words,
          wtimes,
          wdurations,
        }, { lipsyncLang: 'en' });

        return true;
      } catch (err) {
        console.error('[TalkingAvatar] speakAudio:', err);
        return false;
      }
    },
    stopSpeaking: () => {
      try { headRef.current?.stopSpeaking(); } catch (e) { /* ignore */ }
    },
    isReady: () => status === 'ready',
    isUsingFallback: () => false,
  }));

  if (!visible) return null;

  return (
    <div style={{
      width: '100%', height: '100%', position: 'relative',
      overflow: 'hidden', background: background, borderRadius: '1rem',
    }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} data-testid="talking-avatar-canvas" />

      {status !== 'ready' && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          color: '#F4F7FB',
          fontSize: '0.85rem', gap: 8,
          background: 'rgba(255, 249, 240, 0.95)',
        }} data-testid="avatar-status">
          {status === 'error' ? (
            <span style={{ opacity: 0.6, textAlign: 'center', padding: '0 1rem', lineHeight: 1.5 }}>
              {avatarLang === 'nl'
                ? 'Avatar kon niet laden.\nControleer je internetverbinding en probeer Chrome of Edge.'
                : 'Avatar failed to load.\nCheck your internet and try Chrome or Edge.'}
            </span>
          ) : (
            <>
              <div style={{
                width: 28, height: 28, border: '3px solid rgba(180, 83, 9, 0.15)',
                borderTopColor: '#D97706', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
              <span style={{ opacity: 0.6 }}>
                {/^\d/.test(status) ? status : (avatarLang === 'nl' ? 'Emma laden...' : 'Loading Emma...')}
              </span>
            </>
          )}
        </div>
      )}

      {showNameBadge && status === 'ready' && (
        <div style={{
          position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(120, 53, 15, 0.7)', backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          color: '#FFF', padding: '6px 18px', borderRadius: '2rem',
          fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.03em',
        }} data-testid="avatar-name-badge">
          {config.name}
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
});

TalkingAvatar.displayName = 'TalkingAvatar';
export default TalkingAvatar;
